// =============================================================================
// UTILIDADES DE FORMATEO
// =============================================================================

/**
 * Formatear cantidad de dinero a string
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0.00';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Formatear número con separadores de miles
 */
export const formatNumber = (number, locale = 'en-US') => {
  if (number === null || number === undefined || isNaN(number)) {
    return '0';
  }

  return new Intl.NumberFormat(locale).format(number);
};

/**
 * Formatear porcentaje
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Fecha local en formato YYYY-MM-DD. No usar toISOString(): devuelve la fecha en
 * UTC, y en zonas detrás de UTC (EE.UU.) desde la tarde ya es "mañana".
 */
export const toLocalDateString = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Parsear una fecha. Las fechas sin hora (YYYY-MM-DD) se interpretan como LOCALES;
 * new Date('YYYY-MM-DD') las toma como medianoche UTC y se muestran un día antes.
 */
export const parseLocalDate = (value) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
};

/**
 * Precio de un producto para la categoría de un cliente, según su Estructura de
 * Precios. Mismo criterio que el backend (pricing_service.py), que es quien fija
 * el precio al guardar: la categoría del cliente; si el producto no tiene precio
 * para ella, el de TIENDA; si tampoco, el precio de venta. Un cliente sin
 * categoría cuenta como TIENDA.
 *
 * `producto` es el objeto transformado de useProductos (preciosCategoria, precio).
 */
export const precioParaCategoria = (producto, categoria) => {
  if (!producto) return 0;
  const precios = producto.preciosCategoria || [];
  const buscar = (codigo) => precios.find((cp) => cp.category_code === codigo);
  const encontrado = buscar(categoria || 'TIENDA') || buscar('TIENDA');
  return encontrado ? parseFloat(encontrado.price) : (parseFloat(producto.precio) || 0);
};

/**
 * Formatear fecha a string legible
 */
export const formatDate = (date, format = 'short') => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseLocalDate(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';

  const options = {
    short: { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    },
    long: { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    },
    dateTime: { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
  };

  return dateObj.toLocaleDateString('es-ES', options[format] || options.short);
};

/**
 * Formatear tiempo relativo (hace 2 días, etc.)
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);

  if (diffInSeconds < 60) return 'Hace menos de un minuto';
  if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} minutos`;
  if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} horas`;
  if (diffInSeconds < 604800) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
  if (diffInSeconds < 2419200) return `Hace ${Math.floor(diffInSeconds / 604800)} semanas`;
  
  return formatDate(dateObj);
};

/**
 * Formatear tamaño de archivo
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Capitalizar primera letra
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Convertir a title case
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Truncar texto con ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
};

/**
 * Generar iniciales de un nombre
 */
export const getInitials = (name) => {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Formatear número de teléfono
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remover todos los caracteres no numéricos
  const cleaned = phone.replace(/\D/g, '');
  
  // Formatear como (XXX) XXX-XXXX si tiene 10 dígitos
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  return phone;
};

/**
 * Limpiar y formatear texto de entrada
 */
export const cleanInput = (value) => {
  if (!value) return '';
  return value.trim().replace(/\s+/g, ' ');
};

/**
 * Generar slug URL-friendly
 */
export const generateSlug = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .trim()
    .replace(/[áàäâ]/g, 'a')
    .replace(/[éèëê]/g, 'e')
    .replace(/[íìïî]/g, 'i')
    .replace(/[óòöô]/g, 'o')
    .replace(/[úùüû]/g, 'u')
    .replace(/[ñ]/g, 'n')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

// =============================================================================
// UTILIDADES DE VALIDACIÓN
// =============================================================================

/**
 * Validar email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validar teléfono
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  return phoneRegex.test(phone);
};

/**
 * Validar que el valor sea un número válido
 */
export const isValidNumber = (value) => {
  return !isNaN(value) && isFinite(value);
};

/**
 * Normalizar separador decimal (coma → punto)
 * "1000,50" → "1000.50", "0,002" → "0.002"
 */
export const normalizeDecimal = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(',', '.');
};

/**
 * parseFloat seguro que maneja comas como separador decimal
 * safeParseFloat("0,002") → 0.002
 * safeParseFloat("1000,50") → 1000.50
 */
export const safeParseFloat = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? fallback : parsed;
};

/**
 * Validar que el precio sea válido
 */
export const isValidPrice = (price) => {
  const numPrice = safeParseFloat(price);
  return isValidNumber(numPrice) && numPrice >= 0;
};

/**
 * Validar longitud de texto
 */
export const isValidLength = (text, min = 0, max = Infinity) => {
  if (!text) return min === 0;
  return text.length >= min && text.length <= max;
};

/**
 * Validar que el campo sea requerido
 */
export const isRequired = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

// =============================================================================
// UTILIDADES DE MANIPULACIÓN DE DATOS
// =============================================================================

/**
 * Remover elementos duplicados de un array
 */
export const removeDuplicates = (array, key = null) => {
  if (!Array.isArray(array)) return [];
  
  if (key) {
    const seen = new Set();
    return array.filter(item => {
      const value = item[key];
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  }
  
  return [...new Set(array)];
};

/**
 * Ordenar array de objetos por una propiedad
 */
export const sortBy = (array, key, direction = 'asc') => {
  if (!Array.isArray(array)) return [];
  
  return [...array].sort((a, b) => {
    let aVal = a[key];
    let bVal = b[key];
    
    // Manejar strings
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (direction === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });
};

/**
 * Agrupar array de objetos por una propiedad
 */
export const groupBy = (array, key) => {
  if (!Array.isArray(array)) return {};
  
  return array.reduce((groups, item) => {
    const groupKey = item[key];
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
    return groups;
  }, {});
};

/**
 * Filtrar array de objetos por múltiples criterios
 */
export const multiFilter = (array, filters) => {
  if (!Array.isArray(array)) return [];
  
  return array.filter(item => {
    return Object.keys(filters).every(key => {
      if (!filters[key]) return true; // Si el filtro está vacío, no filtrar
      
      const itemValue = item[key];
      const filterValue = filters[key];
      
      if (typeof itemValue === 'string') {
        return itemValue.toLowerCase().includes(filterValue.toLowerCase());
      }
      
      return itemValue === filterValue;
    });
  });
};

/**
 * Paginar array
 */
export const paginate = (array, page = 1, pageSize = 10) => {
  if (!Array.isArray(array)) return { data: [], totalPages: 0, currentPage: 1 };
  
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  
  return {
    data: array.slice(startIndex, endIndex),
    totalPages: Math.ceil(array.length / pageSize),
    currentPage: page,
    totalItems: array.length
  };
};

// =============================================================================
// UTILIDADES DE ALMACENAMIENTO
// =============================================================================

/**
 * Guardar en localStorage con manejo de errores
 */
export const saveToStorage = (key, value) => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
    return true;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

/**
 * Obtener de localStorage con manejo de errores
 */
export const getFromStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return defaultValue;
  }
};

/**
 * Remover de localStorage
 */
export const removeFromStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error removing from localStorage:', error);
    return false;
  }
};

// =============================================================================
// UTILIDADES DE ARCHIVOS
// =============================================================================

/**
 * Descargar archivo desde blob
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Validar tipo de archivo
 */
export const isValidFileType = (file, allowedTypes) => {
  if (!file || !allowedTypes) return false;
  return allowedTypes.includes(file.type);
};

/**
 * Validar tamaño de archivo
 */
export const isValidFileSize = (file, maxSizeInMB) => {
  if (!file) return false;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

// =============================================================================
// UTILIDADES DIVERSAS
// =============================================================================

/**
 * Generar ID único
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Copiar texto al portapapeles
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};

/**
 * Generar color aleatorio
 */
export const generateRandomColor = () => {
  const colors = [
    '#4A90E2', '#7ED321', '#F5A623', '#D0021B',
    '#9013FE', '#50E3C2', '#B8E986', '#4BD5EE'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Verificar si estamos en modo desarrollo
 */
export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Log solo en desarrollo
 */
export const devLog = (...args) => {
  if (isDevelopment()) {
    console.log(...args);
  }
};
